/**
 * useAdvancedStats Hook
 *
 * Calculates comprehensive trading statistics from completed trades.
 * Includes risk metrics like Sharpe ratio, max drawdown, and expectancy.
 */

import { useMemo } from "react";
import type { CompletedTrade, AdvancedStats } from "@nordex/core";

/**
 * Calculate advanced trading statistics
 */
function calculateAdvancedStats(trades: CompletedTrade[]): AdvancedStats {
  if (!trades.length) {
    return {
      totalTrades: 0,
      winningTrades: 0,
      losingTrades: 0,
      breakEvenTrades: 0,
      winRate: 0,
      totalPnl: 0,
      totalNetPnl: 0,
      totalFees: 0,
      totalVolume: 0,
      averageProfit: 0,
      averageLoss: 0,
      averageTrade: 0,
      largestWin: 0,
      largestLoss: 0,
      profitFactor: 0,
      expectancy: 0,
      riskRewardRatio: 0,
      maxDrawdown: 0,
      maxDrawdownPercent: 0,
      recoveryFactor: 0,
      sharpeRatio: null,
      sortinoRatio: null,
      currentStreak: 0,
      currentStreakType: "none",
      longestWinStreak: 0,
      longestLossStreak: 0,
      avgTradeDurationMs: 0,
      tradingDays: 0,
      tradesPerDay: 0,
    };
  }

  // Sort trades chronologically for streak/drawdown calculation
  const sortedTrades = [...trades].sort(
    (a, b) => a.exitTime.getTime() - b.exitTime.getTime()
  );

  // Basic counts
  let winningTrades = 0;
  let losingTrades = 0;
  let breakEvenTrades = 0;
  let totalProfit = 0;
  let totalLoss = 0;
  let largestWin = 0;
  let largestLoss = 0;
  let totalPnl = 0;
  let totalNetPnl = 0;
  let totalFees = 0;
  let totalVolume = 0;
  let totalDurationMs = 0;

  // Track unique trading days
  const tradingDaysSet = new Set<string>();

  // Streak tracking
  let currentStreak = 0;
  let currentStreakType: "win" | "loss" | "none" = "none";
  let longestWinStreak = 0;
  let longestLossStreak = 0;
  let tempWinStreak = 0;
  let tempLossStreak = 0;

  // Drawdown tracking
  let peak = 0;
  let runningPnl = 0;
  let maxDrawdown = 0;
  let maxDrawdownPercent = 0;

  // Daily returns for Sharpe/Sortino
  const dailyReturns = new Map<string, number>();

  for (const trade of sortedTrades) {
    const pnl = trade.netPnl;
    totalPnl += trade.totalPnl;
    totalNetPnl += trade.netPnl;
    totalFees += trade.fees;
    totalVolume += trade.volume;
    totalDurationMs += trade.durationMs;

    // Track trading day
    const dayStr = trade.exitTime.toISOString().split("T")[0];
    tradingDaysSet.add(dayStr);

    // Track daily returns
    const existingReturn = dailyReturns.get(dayStr) || 0;
    dailyReturns.set(dayStr, existingReturn + pnl);

    // Win/loss classification
    if (pnl > 0) {
      winningTrades++;
      totalProfit += pnl;
      if (pnl > largestWin) largestWin = pnl;

      // Streak tracking
      tempWinStreak++;
      tempLossStreak = 0;
      if (tempWinStreak > longestWinStreak) longestWinStreak = tempWinStreak;
    } else if (pnl < 0) {
      losingTrades++;
      const loss = Math.abs(pnl);
      totalLoss += loss;
      if (loss > largestLoss) largestLoss = loss;

      // Streak tracking
      tempLossStreak++;
      tempWinStreak = 0;
      if (tempLossStreak > longestLossStreak) longestLossStreak = tempLossStreak;
    } else {
      breakEvenTrades++;
      tempWinStreak = 0;
      tempLossStreak = 0;
    }

    // Drawdown calculation
    runningPnl += pnl;
    if (runningPnl > peak) {
      peak = runningPnl;
    }
    const drawdown = peak - runningPnl;
    if (drawdown > maxDrawdown) {
      maxDrawdown = drawdown;
      maxDrawdownPercent = peak > 0 ? (drawdown / peak) * 100 : 0;
    }
  }

  // Current streak
  if (tempWinStreak > 0) {
    currentStreak = tempWinStreak;
    currentStreakType = "win";
  } else if (tempLossStreak > 0) {
    currentStreak = tempLossStreak;
    currentStreakType = "loss";
  }

  const totalTrades = trades.length;
  const winRate = totalTrades > 0 ? (winningTrades / totalTrades) * 100 : 0;
  const averageProfit = winningTrades > 0 ? totalProfit / winningTrades : 0;
  const averageLoss = losingTrades > 0 ? totalLoss / losingTrades : 0;
  const averageTrade = totalTrades > 0 ? totalNetPnl / totalTrades : 0;

  // Profit factor
  const profitFactor =
    totalLoss > 0 ? totalProfit / totalLoss : totalProfit > 0 ? Infinity : 0;

  // Expectancy
  const winRateDecimal = winRate / 100;
  const lossRateDecimal = 1 - winRateDecimal;
  const expectancy =
    winRateDecimal * averageProfit - lossRateDecimal * averageLoss;

  // Risk/Reward ratio
  const riskRewardRatio = averageLoss > 0 ? averageProfit / averageLoss : 0;

  // Recovery factor
  const recoveryFactor = maxDrawdown > 0 ? totalNetPnl / maxDrawdown : 0;

  // Trading frequency
  const tradingDays = tradingDaysSet.size;
  const tradesPerDay = tradingDays > 0 ? totalTrades / tradingDays : 0;

  // Average duration
  const avgTradeDurationMs =
    totalTrades > 0 ? totalDurationMs / totalTrades : 0;

  // Sharpe & Sortino ratios (annualized)
  let sharpeRatio: number | null = null;
  let sortinoRatio: number | null = null;

  if (dailyReturns.size >= 5) {
    const returns = Array.from(dailyReturns.values());
    const avgReturn = returns.reduce((a, b) => a + b, 0) / returns.length;
    const variance =
      returns.reduce((sum, r) => sum + Math.pow(r - avgReturn, 2), 0) /
      returns.length;
    const stdDev = Math.sqrt(variance);

    // Sharpe (assuming 0 risk-free rate for crypto)
    if (stdDev > 0) {
      sharpeRatio = (avgReturn / stdDev) * Math.sqrt(252); // Annualized
    }

    // Sortino (only downside deviation)
    const downsideReturns = returns.filter((r) => r < 0);
    if (downsideReturns.length > 0) {
      const downsideVariance =
        downsideReturns.reduce((sum, r) => sum + Math.pow(r, 2), 0) /
        downsideReturns.length;
      const downsideDev = Math.sqrt(downsideVariance);
      if (downsideDev > 0) {
        sortinoRatio = (avgReturn / downsideDev) * Math.sqrt(252);
      }
    }
  }

  return {
    totalTrades,
    winningTrades,
    losingTrades,
    breakEvenTrades,
    winRate,
    totalPnl,
    totalNetPnl,
    totalFees,
    totalVolume,
    averageProfit,
    averageLoss,
    averageTrade,
    largestWin,
    largestLoss,
    profitFactor,
    expectancy,
    riskRewardRatio,
    maxDrawdown,
    maxDrawdownPercent,
    recoveryFactor,
    sharpeRatio,
    sortinoRatio,
    currentStreak,
    currentStreakType,
    longestWinStreak,
    longestLossStreak,
    avgTradeDurationMs,
    tradingDays,
    tradesPerDay,
  };
}

/**
 * Hook that calculates advanced trading statistics
 */
export function useAdvancedStats(trades: CompletedTrade[]): AdvancedStats {
  return useMemo(() => calculateAdvancedStats(trades), [trades]);
}

